<div class="mobile-settings-page">
  <p-toast></p-toast>

  <header class="page-header">
    <app-back-button title="adminMobileSettings.title" parentRoute="/admin"></app-back-button>
    <div class="header-actions">
      <p>{{ 'adminMobileSettings.subtitle' | translate }}</p>
      <app-primary-button
        [title]="'adminMobileSettings.add'"
        [icon]="'pi pi-plus'"
        [disable]="loading() || !canAdd()"
        (action)="openCreateDialog()"
      ></app-primary-button>
      <app-secondary-button
        class="header-actions__refresh"
        [title]="'Refresh'"
        [icon]="'refresh'"
        [disable]="loading()"
        (action)="loadData()"
      ></app-secondary-button>
    </div>
  </header>

  @if (loadError()) {
    <div class="load-error">
      <i class="pi pi-times-circle"></i>
      <div>
        <strong>{{ 'adminMobileSettings.loadFailed' | translate }}</strong>
        <pre>{{ loadError() }}</pre>
      </div>
    </div>
  }

  <app-data-table
    [columns]="tableColumns()"
    [data]="items()"
    [config]="tableConfig"
    [loading]="loading()"
  ></app-data-table>

  <ng-template #platformTemplate let-item>
    <span class="platform-badge" [class.platform-ios]="item.platform === 'IOS'">
      <i class="pi" [ngClass]="item.platform === 'IOS' ? 'pi-apple' : 'pi-android'"></i>
      {{ item.platform }}
    </span>
  </ng-template>

  <ng-template #minVersionTemplate let-item>
    <code>{{ item.minSupportedVersion || '—' }}</code>
    @if (item._misconfigured) {
      <i
        class="pi pi-exclamation-triangle warn-icon"
        [pTooltip]="'adminMobileSettings.errors.gateInverted' | translate"
      ></i>
    }
  </ng-template>

  <ng-template #storeUrlTemplate let-item>
    @if (item.storeUrl) {
      <a [href]="item.storeUrl" target="_blank" rel="noopener noreferrer">{{ item.storeUrl }}</a>
    } @else {
      <span class="muted">{{ 'adminMobileSettings.noStoreUrl' | translate }}</span>
    }
  </ng-template>

  <ng-template #rowActionsTemplate let-item>
    <app-table-actions
      [row]="item"
      (editAction)="openEditDialog($event)"
      (deleteAction)="askDelete($event)"
    ></app-table-actions>
  </ng-template>

  <p class="hint">
    <i class="pi pi-info-circle"></i>
    {{ 'adminMobileSettings.hint' | translate }}
  </p>
</div>

<!-- Create / edit -->
<p-dialog
  [visible]="dialogVisible()"
  (visibleChange)="onDialogVisibleChange($event)"
  [modal]="true"
  [draggable]="false"
  [resizable]="false"
  [style]="{ width: '520px' }"
  [header]="
    (dialogMode() === 'create' ? 'adminMobileSettings.addTitle' : 'adminMobileSettings.editTitle')
      | translate
  "
>
  <div class="settings-form">
    <div class="form-field">
      <label>{{ 'adminMobileSettings.platform' | translate }} *</label>
      @if (dialogMode() === 'edit') {
        <!-- A row is bound to its platform: nothing to pick, just show which one is edited. -->
        <span class="platform-badge" [class.platform-ios]="form.platform === 'IOS'">
          <i class="pi" [ngClass]="form.platform === 'IOS' ? 'pi-apple' : 'pi-android'"></i>
          {{ form.platform }}
        </span>
      } @else {
        <msf-select
          [options]="availablePlatforms()"
          [(model)]="form.platform"
          (modelChange)="onFormChange()"
          optionLabel="label"
          optionValue="value"
          translateBy="none"
          [placeholder]="'adminMobileSettings.selectPlatform' | translate"
        ></msf-select>
      }
      @if (submitted() && platformError()) {
        <small class="field-error">{{ platformError()! | translate }}</small>
      }
    </div>

    <div class="form-field">
      <label>{{ 'adminMobileSettings.latestVersion' | translate }} *</label>
      <msf-input
        [(model)]="form.latestVersion"
        (modelChange)="onFormChange()"
        placeholder="1.4.0"
      ></msf-input>
      <small class="field-hint">{{ 'adminMobileSettings.latestVersionHint' | translate }}</small>
      @if (submitted() && latestVersionError()) {
        <small class="field-error">{{ latestVersionError()! | translate }}</small>
      }
    </div>

    <div class="form-field">
      <label>{{ 'adminMobileSettings.minSupportedVersion' | translate }} *</label>
      <msf-input
        [(model)]="form.minSupportedVersion"
        (modelChange)="onFormChange()"
        placeholder="1.2.0"
      ></msf-input>
      <small class="field-hint">{{ 'adminMobileSettings.minSupportedVersionHint' | translate }}</small>
      @if (submitted() && minSupportedVersionError()) {
        <small class="field-error">{{ minSupportedVersionError()! | translate }}</small>
      }
    </div>

    <div class="form-field">
      <label>{{ 'adminMobileSettings.storeUrl' | translate }}</label>
      <msf-input
        [(model)]="form.storeUrl"
        (modelChange)="onFormChange()"
        placeholder="https://play.google.com/store/apps/details?id=..."
      ></msf-input>
      <small class="field-hint">{{ 'adminMobileSettings.storeUrlHint' | translate }}</small>
      @if (submitted() && storeUrlError()) {
        <small class="field-error">{{ storeUrlError()! | translate }}</small>
      }
    </div>

    @if (gateWarning()) {
      <div class="form-warning">
        <i class="pi pi-exclamation-triangle"></i>
        {{ 'adminMobileSettings.errors.gateInverted' | translate }}
      </div>
    }

    <div class="form-actions">
      <app-secondary-button
        [title]="'Cancel'"
        [disable]="saving()"
        (action)="closeDialog()"
      ></app-secondary-button>
      <app-save-button [isSaving]="saving()" (save)="save()"></app-save-button>
    </div>
  </div>
</p-dialog>

<!-- Delete confirmation -->
<p-dialog
  [visible]="deleteModalVisible()"
  (visibleChange)="onDeleteModalVisibleChange($event)"
  [modal]="true"
  [draggable]="false"
  [resizable]="false"
  [style]="{ width: '440px' }"
  [header]="'adminMobileSettings.deleteTitle' | translate"
>
  <div class="delete-body">
    <p class="delete-question">
      {{ 'adminMobileSettings.deleteQuestion' | translate }}
      <strong>{{ itemToDelete?.platform }}</strong>
    </p>
    <p class="delete-note">{{ 'adminMobileSettings.deleteNote' | translate }}</p>

    <div class="form-actions">
      <app-secondary-button
        [title]="'Cancel'"
        [disable]="deleting()"
        (action)="cancelDelete()"
      ></app-secondary-button>
      <app-primary-button
        [title]="'Delete'"
        [icon]="'pi pi-trash'"
        [loading]="deleting()"
        (action)="confirmDelete()"
      ></app-primary-button>
    </div>
  </div>
</p-dialog>
